import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { speak, speechSupport, stopSpeaking } from '../../audio/speech';

/**
 * Панель аудіозавдання: кнопка «Прослухати» замість тексту фрази.
 *
 * Головна вимога — текст англійської фрази НЕ повинен потрапити на екран.
 * Інакше завдання перестає бути аудіюванням: дитина просто прочитає
 * відповідь (див. audio/speech.ts).
 *
 * Виняток один: коли синтезу мови на пристрої немає. Тоді показуємо текст,
 * бо питання без звуку й без тексту не має розвʼязку взагалі. Про підміну
 * чесно попереджаємо — мовчазна деградація тут найгірший варіант.
 */
export function ListenPanel({ text }: { text: string }): React.JSX.Element {
  const [support] = useState(speechSupport);
  const [speaking, setSpeaking] = useState(false);
  const [played, setPlayed] = useState(false);

  // Перехід до наступного питання обриває фразу: інакше вона доспівує вже
  // над іншим завданням і збиває дитину.
  useEffect(() => {
    setSpeaking(false);
    setPlayed(false);
    return stopSpeaking;
  }, [text]);

  if (support === 'missing') {
    return (
      <Panel tight className="p-4">
        <p className="text-sm leading-relaxed font-semibold text-white/85" data-testid="speech-fallback">
          {text}
        </p>
        <p className="mt-2 text-xs text-white/60">
          Цей браузер не вміє озвучувати. Показуємо фразу текстом.
        </p>
      </Panel>
    );
  }

  return (
    <Panel tight className="p-4">
      <Button
        tone="cyan"
        block
        size="xl"
        data-testid="speak"
        aria-label={played ? 'Прослухати фразу ще раз' : 'Прослухати фразу'}
        onClick={() => {
          setSpeaking(true);
          setPlayed(true);
          speak(text, { onEnd: () => setSpeaking(false) });
        }}
      >
        <Icon name="sound-on" size={22} />
        {speaking ? 'Звучить…' : played ? 'Ще раз' : 'Прослухати'}
      </Button>
      <p className="mt-2 text-center text-xs text-white/60">
        Слухай уважно. Можна вмикати скільки завгодно разів.
      </p>
    </Panel>
  );
}
